import { Component, ViewEncapsulation } from '@angular/core';
import { FormBuilder, Validators, FormGroup, AbstractControl, ValidationErrors } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { timer, Subscription } from 'rxjs';

import { UsuariosApiService } from 'src/app/service/api/usuarios-api.service';
import { PasswordRecoveryService } from 'src/app/service/api/password-recovery.service';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

@Component({
  selector: 'app-password-recovery',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './password-recovery.component.html',
  styleUrls: ['./password-recovery.component.scss'],
  providers: [MessageService]
})
export class PasswordRecoveryComponent {
  /** Branding */
  public readonly logoUrl = environment.logoUrl;

  public readonly env = environment.appName;

  // Steps
  activeIndex = 0;
  
  readonly steps = [
    { label: 'Email' },
    { label: 'Verificación' },
    { label: 'Nueva contraseña' }
  ];

  // Forms
  emailForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]]
  });

  codeForm: FormGroup = this.fb.group({
    code: ['', [Validators.required]]
  });

  passwordForm: FormGroup = this.fb.group({
    password: ['', [Validators.required, Validators.minLength(8), this.hasStrength()]],
    repeat: ['', [Validators.required]]
  }, { validators: [this.passwordsMatch('password', 'repeat')] });

  // UI state
  sendingEmail = false;
  
  verifying = false;
  
  changing = false;

  // Resend cooldown
  resendIn = 0;

  private resendSub?: Subscription;

  // Server state
  private recoveryUserId: string | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly passwordRecoveryApi: PasswordRecoveryService,
    private readonly usuariosApi: UsuariosApiService,
    private readonly messages: MessageService
  ) {
    void fb;
  }

   // Typed getters
  get emailCtrl(): any { return this.emailForm.get('email'); }
  
  get codeCtrl(): any  { return this.codeForm.get('code'); }
  
  get passwordCtrl(): any  { return this.passwordForm.get('password'); }
  
  get repeatCtrl(): any  { return this.passwordForm.get('repeat'); }

  goBack(): void {
    this.router.navigate(['login']);
  }

  // ---- STEP 1: SEND EMAIL ----
  async sendEmail(): Promise<void> {
    if (this.emailForm.invalid || this.sendingEmail) {
      this.emailForm.markAllAsTouched();
      return;
    }

    this.sendingEmail = true;
    const email = this.emailCtrl?.value?.trim();

    try {
      // Tu endpoint actual ya existe en tu código anterior:
      // GET ws/user/sendPasswordRecovery.php?idUsuario=...&email=...
      // Aquí asumimos una llamada de alto nivel (adáptala si la tienes en otro servicio).
      await this.passwordRecoveryApi.sendEmail(email);
      this.toast('success', 'Email enviado', 'Hemos enviado un código de verificación a tu email.');
      this.startResendCooldown();
      this.activeIndex = 1;
    } catch (e) {
      this.toast('error', 'No se pudo enviar', 'Revisa el email e inténtalo de nuevo.');
    } finally {
      this.sendingEmail = false;
    }
  }

  // ---- STEP 2: VERIFY CODE ----
  async verifyCode(): Promise<void> {
    if (this.codeForm.invalid || this.verifying) {
      this.codeForm.markAllAsTouched();
      return;
    }

    this.verifying = true;
    const code = this.codeCtrl?.value;

    try {
      // Tu código anterior:
      // GET ws/user/checkCode.php?code=...
      // Respuesta esperada {result: 1, id_usuario: string}
      const res = await this.passwordRecoveryApi.verify(code); // adapta a tu servicio/endpoint

      if (res?.result === 1) {
        this.recoveryUserId = res.id_usuario;
        this.toast('success', 'Código válido', 'Ahora establece tu nueva contraseña.');
        this.activeIndex = 2;
      } else {
        this.toast('error', 'Código inválido', 'El código no es válido o ha caducado.');
      }
    } catch (e) {
      this.toast('error', 'Error verificando', 'Inténtalo de nuevo.');
    } finally {
      this.verifying = false;
    }
  }

/** STEP 3: Cambiar contraseña con mensajes amigables */
async changePassword(): Promise<void> {
  if (this.passwordForm.invalid || this.changing || this.recoveryUserId == null) {
    this.passwordForm.markAllAsTouched();
    return;
  }

  this.changing = true;
  const password = this.passwordCtrl.value as string;

  try {
    // IMPORTANTE: el endpoint PHP devolverá 200 OK con {message, data} o 204 No Content.
    //                       └─ tu wrapper .toPromise(...) puede devolver null/undefined en 204.
    const res = await this.usuariosApi.usuariosPUT.toPromise({
      id: this.recoveryUserId,
      password
    } as never);

    // Éxito (200 OK) o 204 No Content: ambos NO son error de red.
    if (res === null || res === undefined) {
      // Caso "No Content" (sin cambios aplicados). En la práctica aquí siempre debería actualizar,
      // pero el backend contempla 204. Informamos de forma comprensible.
      this.toast(
        'info',
        'No se han aplicado cambios',
        'Intente de nuevo. Si el problema persiste, contacte con su administrador.'
      );
    } else {
      // OK con contenido (tu backend puede devolver un array vacío u objeto).
      this.toast(
        'success',
        'Contraseña cambiada',
        'Ya puede iniciar sesión con su nueva contraseña.'
      );
      setTimeout(()=> {
        this.goBack();
      }, 1500);
    }
  } catch (err) {
    const { severity, title, body } = this.parseBackendError(err);
    
    this.toast(severity, title, body);
  } finally {
    this.changing = false;
  }
}


  // ---- Resend management ----
  async resend(): Promise<void> {
    if (this.resendIn > 0 || this.sendingEmail) {return;}
    await this.sendEmail();
  }

  toast(severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string): void {
    this.messages.add({ severity, summary, detail, life: 3500 });
  }

  private startResendCooldown(seconds = 45): void {
    this.resendSub?.unsubscribe();
    this.resendIn = seconds;
    this.resendSub = timer(0, 1000).subscribe(() => {
      this.resendIn = Math.max(0, this.resendIn - 1);
      if (this.resendIn === 0) {this.resendSub?.unsubscribe();}
    });
  }

  // ---- Validators ----
  private passwordsMatch(a: string, b: string) {
    return (group: AbstractControl): ValidationErrors | null => {
      const pa = group.get(a)?.value;
      const pb = group.get(b)?.value;
      
      if (!(Boolean(pa)) || !(Boolean(pb))) {return null;}
      return pa === pb ? null : { passwordsNotEqual: true };
    };
  }

  /** Simple strength check: at least 1 upper, 1 lower, 1 number */
  private hasStrength() {
    return (control: AbstractControl): ValidationErrors | null => {
      const v = control.value as string;
      
      if (!v) {return null;}
      const ok =
        /[a-z]/.test(v) &&
        /[A-Z]/.test(v) &&
        /\d/.test(v) &&
        v.length >= 8;
      
        return ok ? null : { weak: true };
    };
  }

  /**
   * Extrae y traduce errores del backend a mensajes comprensibles.
   * Compatible con tu PHP: respond(http, message, data) y códigos 4xx/5xx.
   */
  private parseBackendError(err: unknown): { severity: 'error' | 'warn' | 'info'; title: string; body: string } {
    // Error HTTP estándar de Angular
    const http = err as HttpErrorResponse;

    // 1) Mensaje del backend (si viene en error.message / error.msg / error.error / error.data?.message)
    const backendMessage =
      (http?.error && (http.error.message || http.error.msg || http.error.error)) ??
      (http?.error?.data && (http.error.data.message || http.error.data?.msg)) ??
      (typeof http?.error === 'string' ? http.error : undefined);

    const status = http?.status ?? 0;

    // 2) Mapeo por código de estado + mensaje claro
    switch (status) {
      case 0: // sin conexión / CORS / timeout
        return {
          severity: 'error',
          title: 'No hay conexión',
          body: 'No hemos podido conectar con el servidor. Revise su conexión a Internet e inténtelo de nuevo.'
        };

      case 400: // datos inválidos/faltan parámetros
        return {
          severity: 'warn',
          title: 'Datos incompletos',
          body:
            backendMessage ??
            'Falta información para completar la operación. Revise los datos introducidos e inténtelo de nuevo.'
        };

      case 404: // usuario no encontrado
        return {
          severity: 'warn',
          title: 'Cuenta no localizada',
          body:
            backendMessage ??
            'No hemos encontrado su cuenta. Verifique que el email y el código sean correctos.'
        };

      case 409: // conflicto (p.ej., fallo de migración de tipo, etc.)
        return {
          severity: 'error',
          title: 'No se pudo completar el cambio',
          body:
            backendMessage ??
            'Se ha producido un conflicto al actualizar la información. Inténtelo de nuevo o contacte con su administrador.'
        };

      case 500: // error interno
        return {
          severity: 'error',
          title: 'Error del servidor',
          body:
            backendMessage ??
            'Ha ocurrido un error inesperado. Inténtelo de nuevo más tarde o contacte con su administrador.'
        };

      // 204 suele llegar como éxito (no error). Si llega aquí, tratamos genérico.
      case 204:
        return {
          severity: 'info',
          title: 'Sin cambios',
          body: 'No se ha aplicado ningún cambio. Inténtelo de nuevo.'
        };

      default:
        // Cualquier otro código: mostramos el mensaje del servidor si existe
        return {
          severity: 'error',
          title: 'No se pudo cambiar la contraseña',
          body:
            backendMessage ??
            'Se ha producido un problema al cambiar la contraseña. Inténtelo de nuevo en unos instantes.'
        };
    }
  }
}
